import { faExpand } from '@fortawesome/pro-regular-svg-icons/faExpand';
import pnMarkerIcon from '@propertypal/shared/src/resources/icons/map/property-news-map-icon.svg?url';
import { DEFAULT_COORD, DEFAULT_ZOOM } from '@propertypal/web-app/src/components/map/GoogleMap';
import useTouchDevice from '@propertypal/web-app/src/hooks/useTouchDevice';
import { FeatureCollection } from 'geojson';
import L from 'leaflet';
import React, { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { MapContainer, ExpandButton } from './LeaftletMap.style';

export type LeafletMarker = {
  lat: number;
  lng: number;
  name: string;
  searched?: boolean;
};

interface LeafletMapProps extends Partial<L.MapOptions> {
  lat?: number;
  lng?: number;
  zoom?: number;
  onExpand?: () => void;
  markers?: LeafletMarker[];
  polygons?: Record<any, any>;
}

const LeafletMap: React.FC<LeafletMapProps> = ({
  lat,
  lng,
  zoom = DEFAULT_ZOOM,
  onExpand,
  markers,
  polygons,
  ...props
}) => {
  const theme = useTheme();
  const touchDevice = useTouchDevice();

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);

  useEffect(() => {
    mapInstance.current =
      mapRef.current &&
      L.map(mapRef.current, {
        zoomControl: !touchDevice,
        center: [lat || 0, lng || 0],
        zoom,
        minZoom: 6,
        maxZoom: 20,
        dragging: !touchDevice,
        scrollWheelZoom: false,
        boxZoom: false,
        keyboard: false,
        attributionControl: false,
        ...props,
      });

    if (mapInstance.current) {
      // set tiles
      L.tileLayer('https://tiles.propertypal.com/tile/{z}/{x}/{y}.png')
        .addTo(mapInstance.current)
        .on('tileloadstart', (event) => {
          event.tile.setAttribute('loading', 'lazy');
        });

      // Add marker if lat and lng are defined
      if (lat != null && lng != null) {
        L.marker([lat, lng], {
          icon: L.icon({
            iconUrl: pnMarkerIcon.src,
            iconSize: [44, 44],
            iconAnchor: [22, 44],
          }),
        }).addTo(mapInstance.current);
      }

      // add additional markers
      if (markers?.length) {
        markers.forEach((marker) => {
          L.marker([marker.lat, marker.lng], {
            icon: L.divIcon({
              className: '',
              html: `<div class="marker-relative">
                      <div class="marker-pin">
                        <img src="/map/${marker.searched ? `searched-important-place` : `important-place-marker`}.svg">
                        <div class="marker-pill${marker.searched ? ` marker-searched` : ''}">
                          ${marker.name}
                        </div>
                      </div>
                    </div>`,
              iconSize: [32, 32],
              iconAnchor: [16, 32],
            }),
          }).addTo(mapInstance!.current!);
        });
      }

      // Add GeoJSON layer for polygons
      if (polygons) {
        const geoJsonLayer = L.geoJSON(polygons as FeatureCollection, {
          style: (feature) => {
            return {
              color: feature?.properties?.strokeColor || theme.primary,
              weight: feature?.properties?.strokeWeight || 2,
              fillColor: feature?.properties?.fillColor || theme.primary,
              fillOpacity: feature?.properties?.fillOpacity || 0.5,
              fill: true,
              fillRule: 'nonzero',
            };
          },
        }).addTo(mapInstance.current);

        const bounds = geoJsonLayer?.getBounds();

        // Fit the map to the GeoJSON layer
        if (bounds.isValid()) mapInstance.current.fitBounds(bounds);
        else mapInstance.current.panTo(new L.LatLng(lat || DEFAULT_COORD.latitude, lng || DEFAULT_COORD.longitude));
      }
    }

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [lat, lng, zoom, touchDevice, markers, polygons]);

  return (
    <MapContainer data-testid="leaflet-map" ref={mapRef}>
      {onExpand && (
        <ExpandButton onClick={onExpand} className="pp-button" data-testid="leaflet-map-expand">
          <FontAwesomeIcon icon={faExpand} size={20} />
          <span>Expand</span>
        </ExpandButton>
      )}
    </MapContainer>
  );
};

export default LeafletMap;
